import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import sortBy from 'lodash/sortBy';
import { TaskSeverityLevels } from 'src/constants/TaskSeverityLevels';
import {
    selectFacilityId,
    selectSelectedShiftRid,
    selectStaffMemberId,
} from 'src/store/configSlice';
import { CareTask, useGetCareTasksQuery } from 'src/store/sageAdminApi';
import { getCareTaskCountsByResidentId } from 'src/utils/careTaskUtils';

const isTaskCharted = (careTask: CareTask) => {
    return !!careTask.chartedAtMs;
};

type UseCareTasksParams = {
    enrichTasks?: boolean;
    shiftInstanceRid?: string;
    residentIds?: string[];
    shouldUsePinnedResidents?: boolean;
};

const useCareTasks = ({
    residentIds,
    shouldUsePinnedResidents,
    enrichTasks,
    shiftInstanceRid,
}: UseCareTasksParams) => {
    const currentShiftRid = useSelector(selectSelectedShiftRid);
    const shiftRidToUse = shiftInstanceRid ?? currentShiftRid;

    const facilityRid = useSelector(selectFacilityId);
    const selectedStaffMemberId = useSelector(selectStaffMemberId);

    const { data, isLoading } = useGetCareTasksQuery(
        {
            shiftInstanceId: shiftRidToUse,
            facilityId: facilityRid,
            ...(shouldUsePinnedResidents
                ? { staffMemberId: selectedStaffMemberId }
                : { residentIds }),
            enrichTasks: enrichTasks ?? false,
        },
        {
            skip:
                !shiftRidToUse ||
                !facilityRid ||
                (shouldUsePinnedResidents && !selectedStaffMemberId) ||
                (residentIds !== undefined && !residentIds.length),
        }
    );

    const {
        unresolvedTaskCount,
        resolvedTaskCount,
        unresolvedCareTasks,
        resolvedCareTasks,
        careTaskCountsByResidentId,
    } = useMemo(() => {
        const plannedCareTasks = (data?.careTasks ?? []).filter(
            (task) => task.careTaskType === 'PLANNED'
        );
        const unresolvedCareTasks = plannedCareTasks.filter((task) => !isTaskCharted(task));
        const sortedUnresolvedCareTasks = sortBy(unresolvedCareTasks, [
            (t) => !t.scheduledAtMs,
            (t) => t.scheduledAtMs || 0,
            (t) => t.name || '',
        ]);

        const unresolvedCount = unresolvedCareTasks.length;

        const resolvedCareTasks = plannedCareTasks.filter(isTaskCharted);
        const resolvedCount = resolvedCareTasks.length;

        const careTaskCountsByResidentId = getCareTaskCountsByResidentId(
            unresolvedCareTasks,
            resolvedCareTasks
        );

        return {
            unresolvedTaskCount: unresolvedCount,
            resolvedTaskCount: resolvedCount,
            // There are no cases where we want the unsorted unresolvedCareTasks
            unresolvedCareTasks: sortedUnresolvedCareTasks,
            resolvedCareTasks,
            careTaskCountsByResidentId,
        };
    }, [data]);

    const resolvedCareTasksWithAlerts = useMemo(() => {
        const alertsByTaskId = new Map(
            (data?.tasks ?? [])
                .filter((task) => task.severity === TaskSeverityLevels.Emergency.id)
                .map((task) => [task.id, task])
        );

        return resolvedCareTasks.map((careTask) => ({
            careTask,
            alert: alertsByTaskId.get(careTask.taskAuditedId),
        }));
    }, [resolvedCareTasks, data]);

    const shiftHasTasks = unresolvedTaskCount + resolvedTaskCount > 0;

    return {
        unresolvedTaskCount,
        resolvedTaskCount,
        shiftHasTasks,
        careTasks: data?.careTasks ?? [],
        unresolvedCareTasks,
        resolvedCareTasks,
        careTaskCountsByResidentId,
        resolvedCareTasksWithAlerts,
        isLoading,
    };
};

export default useCareTasks;
